import * as React from "react";
import { FolderArrowRight20Regular } from "@fluentui/react-icons";
import { apiUrl } from "../../utils/api";
import { documentService } from "../../services/documentService";
import { kundenakteOrdnerService } from "../../services/kundenakteOrdnerService";
import { dvagPostService } from "../../services/dvagPostService";
import { KontaktSuchfeld } from "../forms/widgets/KontaktSuchfeld";
import { AkteZuweisenPopup } from "../alteKundenakten/AkteZuweisenPopup";
import { OpenWindowConfig, useWindowManager } from "../windowManager/WindowManagerContext";
import { OrdnerNavigator, ordnerTitel } from "../documents/explorer/OrdnerNavigator";
import { teileDateiname } from "../documents/DateinameKasten";

/** Kategorie, unter der der Reiter „Dokumente" den Ordnerbaum der Akte führt. */
const KUNDENAKTE = "Alte Kundenakte";

/** Ordner, in dem Post üblicherweise liegt — wird beim Start geöffnet, wenn es ihn gibt. */
const POST_ORDNER = /^(schriftverkehr|korrespondenz|dvag)/i;

export type OpenDvagPostAblageParams = {
    eintragId: number;
    /** Name des PDFs in der Kontakthistorie — im Feld „Dateiname“ vorbelegt und änderbar (T-396). */
    dateiname?: string | null;
    /** Zugewiesener oder vorgeschlagener Kontakt — vorbelegt, bleibt änderbar. */
    kontakt?: { id: number; name: string } | null;
    /** Abgelegt; der Eintrag ist aus der Kontakthistorie verschwunden. */
    onAbgelegt: (ergebnis: { contactId: number; kontaktName: string; ordner: string }) => void;
};

type Kontakt = { id: number; name: string };

/**
 * T-298: Ein Eintrag der DVAG-Kontakthistorie wird gezielt beim Kunden abgelegt
 * — Kontakt wählen, Ordner der Kundenakte wählen, ablegen. Danach verschwindet
 * der Eintrag samt Original-PDF aus der Kontakthistorie.
 *
 * Aufbau und Bausteine wie die Schaden-Ablage (`SchadenAblageFenster`):
 * Kontakt (Pflicht) → ohne Kundenakte einmal fragen, ob eine zugewiesen werden
 * soll → durch die Ordner navigieren, beliebig tief, auch neu anlegen.
 *
 * T-396: Der Dateiname lässt sich vor dem Ablegen ändern; bearbeitet wird nur
 * der Teil vor der Endung (wie beim Umbenennen im PDF-Viewer).
 */
export const DvagPostAblageFenster: React.FC<OpenDvagPostAblageParams & { windowId: string }> = ({
    windowId,
    eintragId,
    dateiname,
    kontakt: vorbelegt,
    onAbgelegt,
}) => {
    const { closeWindow } = useWindowManager();
    const { stamm: stammAlt, endung } = teileDateiname(dateiname ?? "");
    const [stamm, setStamm] = React.useState(stammAlt);
    const [kontakt, setKontakt] = React.useState<Kontakt | null>(vorbelegt ?? null);
    const [laedt, setLaedt] = React.useState(false);
    // Geöffneter Ordner der Akte ("" = Wurzel, sonst mit „/" am Ende).
    const [pfad, setPfad] = React.useState("");
    const [hatAkte, setHatAkte] = React.useState<boolean | null>(null);
    const [akteFrageBeantwortet, setAkteFrageBeantwortet] = React.useState(false);
    const [akteZuweisen, setAkteZuweisen] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [fehler, setFehler] = React.useState<string | null>(null);
    const [ladeTick, setLadeTick] = React.useState(0);

    React.useEffect(() => {
        if (!kontakt) return;
        const ctrl = new AbortController();
        setLaedt(true);
        setFehler(null);
        (async () => {
            try {
                const [wurzel, angelegt, akten] = await Promise.all([
                    documentService.listFolders({ contactId: kontakt.id, prefix: "", category: KUNDENAKTE }, ctrl.signal),
                    kundenakteOrdnerService.liste(kontakt.id, ctrl.signal),
                    fetch(apiUrl("kundenakten-folders", { contactId: String(kontakt.id), limit: 20 }), { signal: ctrl.signal })
                        .then((r) => (r.ok ? r.json() : []))
                        .catch(() => []),
                ]);
                if (ctrl.signal.aborted) return;
                const namen = [
                    ...wurzel.map((f) => f.name),
                    ...angelegt.map((p) => p.split("/")[0]).filter(Boolean),
                ];
                const post = namen.find((n) => POST_ORDNER.test(n.trim()));
                setPfad(post ? `${post.trim()}/` : "");
                setHatAkte(Array.isArray(akten) && akten.length > 0);
            } catch {
                if (!ctrl.signal.aborted) setFehler("Die Ordner des Kontakts konnten nicht geladen werden.");
            } finally {
                if (!ctrl.signal.aborted) setLaedt(false);
            }
        })();
        return () => ctrl.abort();
    }, [kontakt, ladeTick]);

    const akteFrageOffen = !!kontakt && hatAkte === false && !akteFrageBeantwortet;
    const stammNeu = stamm.replace(/\s+/g, " ").trim();
    const nameLeer = !!dateiname && stammNeu === "";
    const umbenannt = !!dateiname && stammNeu !== "" && stammNeu !== stammAlt;
    const kannAblegen = !!kontakt && !busy && !laedt && !akteFrageOffen && !nameLeer;
    const ziel = pfad.replace(/\/+$/, "");

    const ablegen = async () => {
        if (!kontakt || !kannAblegen) return;
        setBusy(true);
        setFehler(null);
        try {
            const res = await dvagPostService.legeBeimKundenAb(eintragId, {
                contactId: kontakt.id,
                ordner: ziel,
                // Nur mitschicken, wenn der Name geändert wurde — sonst bleibt der des Originals.
                ...(umbenannt ? { dateiname: `${stammNeu}${endung}` } : {}),
            });
            onAbgelegt({ contactId: kontakt.id, kontaktName: kontakt.name, ordner: res.ordner });
            closeWindow(windowId);
        } catch (e) {
            setFehler(e instanceof Error && e.message ? e.message : "Das Dokument konnte nicht abgelegt werden.");
            setBusy(false);
        }
    };

    return (
        <div className="kvp-rd" style={wurzelStil}>
            <div style={inhalt}>
                <section>
                    <div style={ueberschrift}>Kontakt</div>
                    {kontakt ? (
                        <div style={chipZeile}>
                            <span style={chip}>{kontakt.name}</span>
                            <button
                                type="button"
                                style={linkKnopf}
                                disabled={busy}
                                onClick={() => {
                                    setKontakt(null);
                                    setHatAkte(null);
                                    setAkteFrageBeantwortet(false);
                                    setPfad("");
                                }}
                            >
                                Anderen Kontakt wählen
                            </button>
                        </div>
                    ) : (
                        <KontaktSuchfeld
                            label="Bei welchem Kontakt soll das Dokument abgelegt werden?"
                            name="dvag-ablage-kontakt"
                            placeholder="Name, Firma oder Kundennummer…"
                            value={null}
                            required
                            autoFocus
                            onChange={(t) => t && setKontakt({ id: t.contactId, name: t.displayName })}
                        />
                    )}
                </section>

                {dateiname && (
                    <section>
                        <div style={ueberschrift}>
                            <label htmlFor={`dvag-ablage-name-${eintragId}`}>Dateiname</label>
                        </div>
                        <div style={namenZeile}>
                            <input
                                id={`dvag-ablage-name-${eintragId}`}
                                type="text"
                                value={stamm}
                                maxLength={190}
                                disabled={busy}
                                spellCheck={false}
                                aria-invalid={nameLeer}
                                style={namenFeld}
                                onChange={(e) => setStamm(e.target.value)}
                                onFocus={(e) => e.currentTarget.select()}
                            />
                            {endung && <span style={endungStil}>{endung}</span>}
                        </div>
                        <div style={nameLeer ? { ...hinweis, color: "#8a2626" } : hinweis}>
                            {nameLeer
                                ? "Bitte einen Dateinamen eingeben."
                                : "Unter diesem Namen liegt das Dokument in der Kundenakte."}
                        </div>
                    </section>
                )}

                {akteFrageOffen && !laedt && (
                    <section style={frageBox} role="group" aria-label="Kundenakte zuweisen">
                        <div style={{ fontWeight: 700, color: "#241522" }}>
                            Diesem Kontakt ist keine Kundenakte zugewiesen.
                        </div>
                        <div style={{ color: "#6b5a66", fontSize: 13, marginTop: 4 }}>
                            Soll jetzt eine Kundenakte vom Netzlaufwerk zugewiesen werden? Das Dokument lässt
                            sich auch ohne ablegen.
                        </div>
                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                            <button type="button" style={knopfPrimaer} onClick={() => setAkteZuweisen(true)}>
                                Ja, Akte zuweisen
                            </button>
                            <button type="button" style={knopfSekundaer} onClick={() => setAkteFrageBeantwortet(true)}>
                                Nein, ohne Akte weiter
                            </button>
                        </div>
                    </section>
                )}

                {kontakt && !akteFrageOffen && (
                    <section>
                        <div style={ueberschrift}>In welchen Ordner?</div>
                        {laedt ? (
                            <div style={hinweis}>Ordner werden geladen…</div>
                        ) : (
                            <OrdnerNavigator
                                contactId={kontakt.id}
                                pfad={pfad}
                                onPfad={setPfad}
                                neuAnlegen
                                neuLadenKey={ladeTick}
                                maxHoehe={260}
                            />
                        )}
                    </section>
                )}

                {fehler && <div role="alert" style={fehlerBox}>{fehler}</div>}
            </div>

            <div style={fuss}>
                <span style={{ flex: 1, color: "#6b5a66", fontSize: 12.5 }}>
                    Das Dokument wird in die Kundenakte gelegt. Danach wird der Eintrag aus der Kontakthistorie
                    gelöscht.
                </span>
                <button type="button" style={knopfSekundaer} disabled={busy} onClick={() => closeWindow(windowId)}>
                    Abbrechen
                </button>
                <button
                    type="button"
                    style={{ ...knopfPrimaer, opacity: kannAblegen ? 1 : 0.5, cursor: kannAblegen ? "pointer" : "default" }}
                    disabled={!kannAblegen}
                    onClick={() => void ablegen()}
                >
                    {busy ? "Wird abgelegt…" : `In „${ordnerTitel(pfad)}“ ablegen`}
                </button>
            </div>

            {akteZuweisen && kontakt && (
                <AkteZuweisenPopup
                    contactId={kontakt.id}
                    suchvorschlag={kontakt.name}
                    onClose={() => setAkteZuweisen(false)}
                    onAssigned={() => {
                        setAkteZuweisen(false);
                        setAkteFrageBeantwortet(true);
                        setLadeTick((t) => t + 1);
                    }}
                />
            )}
        </div>
    );
};

const AUB = "#4d2051";
const LINE = "#ece7f0";
const wurzelStil: React.CSSProperties = { display: "flex", flexDirection: "column", height: "100%", minHeight: 0, fontFamily: "'Barlow', 'Segoe UI', sans-serif", background: "#fff" };
const inhalt: React.CSSProperties = { flex: 1, minHeight: 0, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 18 };
const ueberschrift: React.CSSProperties = { fontWeight: 800, color: "#241522", fontSize: 13.5, marginBottom: 8 };
const chipZeile: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" };
const chip: React.CSSProperties = { background: "#f9efe3", color: "#241522", borderRadius: 999, padding: "5px 12px", fontWeight: 700, fontSize: 13.5 };
const linkKnopf: React.CSSProperties = { border: "none", background: "transparent", color: AUB, cursor: "pointer", fontSize: 13, fontFamily: "inherit", textDecoration: "underline", padding: 0 };
const frageBox: React.CSSProperties = { border: `1px solid #ecd9b8`, background: "#fdf8ee", borderRadius: 12, padding: 14 };
const hinweis: React.CSSProperties = { color: "#6b5a66", fontSize: 13, padding: "6px 2px" };
const namenZeile: React.CSSProperties = { display: "flex", alignItems: "center", gap: 6 };
const namenFeld: React.CSSProperties = { flex: 1, minWidth: 0, border: `1px solid ${LINE}`, background: "#fff", color: "#241522", borderRadius: 10, padding: "8px 12px", fontSize: 13.5, fontFamily: "inherit" };
const endungStil: React.CSSProperties = { color: "#6b5a66", fontSize: 13.5, flexShrink: 0 };
const fehlerBox: React.CSSProperties = { padding: "8px 12px", borderRadius: 10, background: "#fdf0f0", color: "#8a2626", fontSize: 13 };
const fuss: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", borderTop: `1px solid ${LINE}`, flexShrink: 0, flexWrap: "wrap" };
const knopfSekundaer: React.CSSProperties = { border: `1px solid ${LINE}`, background: "#fff", color: "#241522", borderRadius: 10, padding: "8px 14px", fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" };
const knopfPrimaer: React.CSSProperties = { border: `1px solid ${AUB}`, background: AUB, color: "#fff", borderRadius: 10, padding: "8px 14px", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };

/** Öffnet „Beim Kunden ablegen" als Fenster. */
export const openDvagPostAblage = (
    openWindow: (config: OpenWindowConfig) => void,
    params: OpenDvagPostAblageParams,
): string => {
    const windowId = `dvag-post-ablage-${params.eintragId}`;
    openWindow({
        id: windowId,
        title: "Beim Kunden ablegen",
        icon: React.createElement(FolderArrowRight20Regular),
        initialSize: { width: 580, height: 620 },
        center: true,
        render: () => React.createElement(DvagPostAblageFenster, { windowId, ...params }),
    });
    return windowId;
};
